/**
 * 销售与方案设计师共用的要求及讨论（2026-10-28）。
 * 原要求始终可见，补充和留言独立留痕，不借用重新下达流程，避免误改状态、期限或附件。
 * 后台同步只更新消息和权限，不回填输入框；网络失败重试复用 requestId，防止重复留言和提醒。
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Download, FileText, Film, Image, Loader2, MessageSquare, Paperclip, RefreshCw, Send, X } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/textarea";
import { MediaLightbox, mediaKind } from "../../../components/MediaLightbox";
import { useApp } from "../../../context/AppContext";
import { fileUrl, fmtBytes } from "../../../lib/attach";
import { downloadFile } from "../../../lib/fileDownload";
import { T } from "../../../lib/i18n";
import { uploadChunked, uploadErrText, UPLOAD_MAX } from "../../../lib/upload";
import { DESIGN_UPLOAD_ACCEPT, DESIGN_UPLOAD_EXT } from "../../../lib/uploadAccept";
import { fmtCN } from "./DesignPresign";
import FileUploadDate, { uploadDateLabel } from "./FileUploadDate";

const MAX_FILES = 12;
const newRequestId = () => typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
  ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
const fileType = (file) => {
  const name = file.filename || file.name || "";
  // These formats can be uploaded, but browsers cannot reliably display the unconverted original.
  if (/\.(heic|heif|tif|tiff)$/i.test(name)) return "file";
  return mediaKind(name, file.contentType || file.type || "");
};

function AttachmentIcon({ kind }) {
  if (kind === "photo") return <Image className="h-4 w-4 shrink-0" aria-hidden="true" />;
  if (kind === "video") return <Film className="h-4 w-4 shrink-0" aria-hidden="true" />;
  return <FileText className="h-4 w-4 shrink-0" aria-hidden="true" />;
}

/** 合并不可变记录：轮询最新页时保留已展开的旧页，也不让慢请求覆盖刚提交的消息。 */
function mergeMessages(previous, incoming) {
  const byId = new Map(previous.map((message) => [message.id, message]));
  incoming.forEach((message) => byId.set(message.id, message));
  return [...byId.values()].sort((a, b) => (
    String(a.createdAt).localeCompare(String(b.createdAt)) || String(a.id).localeCompare(String(b.id))
  ));
}

export default function PresignRequirementDiscussion({ lid, refreshKey }) {
  const { api, canAction } = useApp();
  const [snapshot, setSnapshot] = useState(null);
  const [messages, setMessages] = useState([]);
  const [older, setOlder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [text, setText] = useState("");
  const [kind, setKind] = useState("requirement");
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [lightbox, setLightbox] = useState(null);
  const epoch = useRef(0);
  const refreshing = useRef(null);
  const submitting = useRef(false);
  const pending = useRef(null);
  const olderInitialized = useRef(false);
  const syncBoundary = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const uploadedFiles = useRef(new Map());
  const uploadAbort = useRef(null);
  const endpoint = `/design/presign/orders/${lid}/discussion`;

  const refresh = useCallback(async () => {
    const current = epoch.current;
    if (refreshing.current === current) return;
    refreshing.current = current;
    try {
      const { data } = await api.get(endpoint);
      if (current !== epoch.current) return;
      let page = data;
      let incoming = data.messages || [];
      // 离开页面后可能新增超过一页：补齐上次同步位置到最新页之间的记录，不能只抓最新50条。
      const boundary = syncBoundary.current;
      while (boundary && page.hasMore && !incoming.some((message) => message.id === boundary)) {
        const result = await api.get(endpoint, { params: { before: page.nextBefore } });
        if (current !== epoch.current) return;
        page = result.data;
        incoming = [...(page.messages || []), ...incoming];
      }
      setSnapshot(data);
      setMessages((previous) => mergeMessages(previous, incoming));
      if (!olderInitialized.current || !boundary) {
        setOlder(page.hasMore ? page.nextBefore : null);
        olderInitialized.current = true;
      }
      syncBoundary.current = data.messages?.[data.messages.length - 1]?.id || null;
      setLoadError("");
    } catch (error) {
      if (current !== epoch.current) return;
      if ([401, 403, 404].includes(error?.response?.status)) {
        setSnapshot(null);
        setMessages([]);
        setOlder(null);
        olderInitialized.current = false;
        syncBoundary.current = null;
      }
      setLoadError(T("方案要求与讨论加载失败，请重试。"));
    } finally {
      if (refreshing.current === current) refreshing.current = null;
      if (current === epoch.current) setLoading(false);
    }
  }, [api, endpoint]);

  useEffect(() => {
    refresh();
    // 双方停留在同一工单时持续同步；回到窗口立即刷新，不重载整张详情或清空手填草稿。
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    const timer = window.setInterval(onVisible, 20000);
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      epoch.current += 1;
      uploadAbort.current?.abort();
      window.clearInterval(timer);
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh]);

  // 下达、改派、关闭等动作完成后立即重取原要求与行级权限。
  useEffect(() => { refresh(); }, [refreshKey, refresh]);

  const canAppend = !!snapshot?.permissions?.canAppendRequirement
    && canAction("/design/presign", "append-requirement") !== false;
  const canComment = !!snapshot?.permissions?.canComment
    && canAction("/design/presign", "comment") !== false;
  const canAttach = !!snapshot?.permissions?.canAttach
    && canAction("/design/presign", "attach-discussion") !== false;
  const activeKind = kind === "requirement" && canAppend ? "requirement" : canComment ? "comment" : "requirement";
  const canWrite = canAppend || canComment;
  const fieldLabel = activeKind === "requirement" ? T("补充方案要求") : T("留言内容");

  const addFiles = (event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";
    if (!selected.length || !canAttach) return;
    if (files.length + selected.length > MAX_FILES) {
      setFeedback({ error: true, text: T("每条讨论最多可添加 {0} 个附件。", [MAX_FILES]) });
      return;
    }
    const oversized = selected.find((file) => file.size > UPLOAD_MAX);
    if (oversized) {
      setFeedback({ error: true, text: T("「{0}」超过 200MB，无法上传。", [oversized.name]) });
      return;
    }
    const empty = selected.find((file) => file.size === 0);
    if (empty) {
      setFeedback({ error: true, text: T("「{0}」是空文件，请重新选择。", [empty.name]) });
      return;
    }
    const unsupported = selected.find((file) => !DESIGN_UPLOAD_EXT.has((file.name.split(".").pop() || "").toLowerCase()));
    if (unsupported) {
      setFeedback({ error: true, text: T("「{0}」的文件类型暂不支持。", [unsupported.name]) });
      return;
    }
    setFiles((previous) => [...previous, ...selected.map((file) => ({ id: newRequestId(), file }))]);
    setFeedback(null);
  };

  const removeFile = (id) => {
    if (sending) return;
    uploadedFiles.current.delete(id);
    setFiles((previous) => previous.filter((item) => item.id !== id));
    setUploadProgress((previous) => {
      const next = { ...previous };
      delete next[id];
      return next;
    });
    setFeedback(null);
  };

  const openFile = (messageFiles, index) => {
    const items = messageFiles.map((file) => ({
      id: file.fileId, fileId: file.fileId, name: file.filename || file.name || T("附件"),
      url: fileUrl(file.fileId), kind: fileType(file), size: file.size, meta: uploadDateLabel(file.uploadedAt),
    }));
    const selected = items[index];
    if (!selected?.fileId) return;
    if (selected.kind === "file") {
      downloadFile(selected.fileId, selected.name).catch(() => setFeedback({ error: true, text: T("附件下载失败，请重试。") }));
      return;
    }
    setLightbox({ items, index });
  };

  const loadOlder = async () => {
    if (!older || loadingOlder) return;
    const current = epoch.current;
    setLoadingOlder(true);
    try {
      const { data } = await api.get(endpoint, { params: { before: older } });
      if (current !== epoch.current) return;
      setMessages((previous) => mergeMessages(previous, data.messages || []));
      setOlder(data.hasMore ? data.nextBefore : null);
      setLoadError("");
    } catch (error) {
      if (current === epoch.current) setLoadError(T("较早记录加载失败，请重试。"));
    } finally {
      if (current === epoch.current) setLoadingOlder(false);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (submitting.current || !canWrite) return;
    const content = text.trim();
    if (!content && (activeKind === "requirement" || files.length === 0)) {
      setFeedback({ error: true, text: activeKind === "requirement"
        ? T("请填写补充要求后再发送。") : T("请填写留言或添加附件后再发送。") });
      inputRef.current?.focus();
      return;
    }
    if (files.length && !canAttach) {
      setFeedback({ error: true, text: T("当前没有发送讨论附件的权限。") });
      return;
    }
    const current = epoch.current;
    const signature = JSON.stringify([activeKind, content, files.map((item) => item.id)]);
    if (pending.current && pending.current.signature !== signature) {
      // A previous POST may have succeeded even if its response was lost. A changed
      // message needs fresh file IDs because the old uploads can now belong to it.
      uploadedFiles.current.clear();
      setUploadProgress({});
    }
    if (!pending.current || pending.current.signature !== signature) {
      pending.current = { signature, requestId: newRequestId() };
    }
    const requestId = pending.current.requestId;
    const controller = new AbortController();
    uploadAbort.current = controller;
    submitting.current = true;
    setSending(true);
    setFeedback(null);
    let stage = "upload";
    try {
      const attached = [];
      for (const item of files) {
        let uploaded = uploadedFiles.current.get(item.id);
        if (!uploaded) {
          setUploadProgress((previous) => ({ ...previous, [item.id]: { pct: 0, state: "uploading" } }));
          try {
            uploaded = await uploadChunked(api, item.file, {
              signal: controller.signal,
              onProgress: ({ pct, state }) => {
                if (current === epoch.current) {
                  setUploadProgress((previous) => ({ ...previous, [item.id]: { pct, state } }));
                }
              },
            });
          } catch (error) {
            if (current === epoch.current) {
              setUploadProgress((previous) => ({ ...previous, [item.id]: { pct: 0, state: "error" } }));
            }
            throw error;
          }
          uploadedFiles.current.set(item.id, uploaded);
        }
        attached.push({
          fileId: uploaded.fileId,
          filename: uploaded.filename || item.file.name,
          contentType: uploaded.contentType || item.file.type || "application/octet-stream",
          size: uploaded.size ?? item.file.size,
        });
        setUploadProgress((previous) => ({ ...previous, [item.id]: { pct: 100, state: "done" } }));
      }
      if (current !== epoch.current) return;
      stage = "post";
      const { data } = await api.post(endpoint, {
        kind: activeKind, text: content, requestId, files: attached,
      }, { signal: controller.signal });
      if (current !== epoch.current) return;
      setMessages((previous) => mergeMessages(previous, data.message ? [data.message] : []));
      pending.current = null;
      setText("");
      setFiles([]);
      setUploadProgress({});
      uploadedFiles.current.clear();
      const notice = data.notification?.status;
      setFeedback({
        error: false,
        text: notice === "sent" ? T("已保存，已发送站内提醒。")
          : notice === "failed" || notice === "partial" ? T("内容已保存，部分站内提醒未发送成功。")
            : T("内容已保存。"),
      });
      refresh();
    } catch (error) {
      if (current !== epoch.current) return;
      setFeedback({ error: true, text: error?.response?.data?.detail
        || (stage === "upload" ? uploadErrText(error) : T("发送失败，内容和附件已保留，请重试。")) });
      if ([400, 403].includes(error?.response?.status)) refresh();
    } finally {
      submitting.current = false;
      if (uploadAbort.current === controller) uploadAbort.current = null;
      if (current === epoch.current) setSending(false);
    }
  };

  return (
    <section className="min-w-0 space-y-3 rounded-lg border border-slate-200 bg-white p-3"
      aria-labelledby="presign-discussion-heading" data-testid="dv3-presign-discussion">
      <div className="flex items-center justify-between gap-2">
        <h3 id="presign-discussion-heading" className="flex min-w-0 items-center gap-1.5 text-sm font-semibold text-slate-800">
          <MessageSquare className="h-4 w-4 shrink-0 text-violet-500" aria-hidden="true" />{T("方案要求与讨论")}
        </h3>
        <Button type="button" size="sm" variant="ghost" onClick={refresh} disabled={loading}
          aria-label={T("刷新方案要求与讨论")} title={T("刷新方案要求与讨论")}>
          <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      </div>
      {loading && <p role="status" className="text-xs text-slate-500">{T("正在加载…")}</p>}
      {loadError && <div role="alert" className="flex flex-wrap items-center gap-2 text-xs text-destructive">
        <span>{loadError}</span><Button type="button" size="sm" variant="outline" onClick={refresh}>{T("重试")}</Button>
      </div>}
      {snapshot && <>
        <div className="rounded-md bg-slate-50 p-2.5" data-testid="dv3-presign-shared-requirement">
          <p className="mb-1 text-xs font-semibold text-slate-700">{T("方案要求")}</p>
          <p className="whitespace-pre-wrap break-words text-xs leading-5 text-slate-700 [overflow-wrap:anywhere]">
            {snapshot.requirement || T("尚未填写方案要求")}
          </p>
        </div>
        {older && <Button type="button" size="sm" variant="outline" className="w-full" onClick={loadOlder} disabled={loadingOlder}>
          {loadingOlder && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}{T("查看更早记录")}
        </Button>}
        {messages.length > 0 ? <ol className="max-h-80 space-y-2 overflow-y-auto" aria-label={T("方案要求与讨论记录")}
          data-testid="dv3-presign-discussion-messages">
          {messages.map((message) => <li key={message.id} className="rounded-md border border-slate-100 p-2.5"
            data-testid={`dv3-presign-message-${message.id}`}>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
              <span className="font-semibold text-slate-700">{message.authorName || message.author}</span>
              <span className={message.kind === "requirement" ? "text-violet-700" : "text-slate-500"}>
                {message.kind === "requirement" ? T("补充要求") : T("留言")}
              </span>
              <time dateTime={message.createdAt} className="text-slate-500">{fmtCN(message.createdAt)}</time>
            </div>
            {message.text && <p className="mt-1 whitespace-pre-wrap break-words text-xs leading-5 text-slate-700 [overflow-wrap:anywhere]">{message.text}</p>}
            {!!message.files?.length && <div className="mt-2 flex flex-wrap gap-2" data-testid={`dv3-presign-message-files-${message.id}`}>
              {message.files.map((file, index) => {
                const name = file.filename || file.name || T("附件");
                const kind = fileType(file);
                return <div key={`${file.fileId}-${index}`} className="flex min-w-0 max-w-full items-center gap-1 rounded-md border border-slate-200 bg-slate-50 p-1">
                  <button type="button" onClick={() => openFile(message.files, index)}
                    className="flex min-w-0 max-w-full items-center gap-1.5 rounded p-1 text-left text-xs text-slate-700 hover:text-brand-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                    aria-label={T("查看附件 {0}", [name])} data-testid={`dv3-presign-message-file-${message.id}-${index}`}>
                    {kind === "photo"
                      ? <span className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded bg-slate-100 text-slate-500">
                        <AttachmentIcon kind={kind} />
                        <img src={fileUrl(file.fileId)} alt="" loading="lazy" onError={(event) => { event.currentTarget.style.display = "none"; }}
                          className="absolute inset-0 h-full w-full rounded object-cover" />
                      </span>
                      : <AttachmentIcon kind={kind} />}
                    <span className="flex min-w-0 max-w-32 flex-col"><span className="truncate">{name}</span><FileUploadDate value={file.uploadedAt} className="block leading-tight" /></span>
                  </button>
                  <button type="button" onClick={() => downloadFile(file.fileId, name).catch(() => setFeedback({ error: true, text: T("附件下载失败，请重试。") }))}
                    className="rounded p-1 text-slate-500 hover:text-brand-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                    aria-label={T("下载附件 {0}", [name])} title={T("下载附件")}
                    data-testid={`dv3-presign-message-download-${message.id}-${index}`}>
                    <Download className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </div>;
              })}
            </div>}
          </li>)}
        </ol> : <p className="text-xs text-slate-500">{T("暂无补充或留言")}</p>}
        {canWrite ? <form onSubmit={submit} className="space-y-2">
          {canAppend && canComment && <div className="flex flex-wrap gap-2" aria-label={T("发送内容类型")}>
            <Button type="button" size="sm" variant={activeKind === "requirement" ? "secondary" : "ghost"}
              aria-pressed={activeKind === "requirement"} disabled={sending} onClick={() => setKind("requirement")}>{T("补充要求")}</Button>
            <Button type="button" size="sm" variant={activeKind === "comment" ? "secondary" : "ghost"}
              aria-pressed={activeKind === "comment"} disabled={sending} onClick={() => setKind("comment")}>{T("留言讨论")}</Button>
          </div>}
          <label htmlFor="presign-discussion-content" className="block text-xs font-semibold text-slate-700">{fieldLabel}</label>
          <Textarea id="presign-discussion-content" ref={inputRef} value={text} onChange={(event) => { setText(event.target.value); setFeedback(null); }}
            rows={3} maxLength={5000} disabled={sending} aria-describedby={feedback ? "presign-discussion-feedback" : undefined}
            className="text-xs" data-testid="dv3-presign-discussion-input" />
          {files.length > 0 && <div className="flex flex-wrap gap-1.5" aria-label={T("待发送附件")} data-testid="dv3-presign-discussion-pending-files">
            {files.map((item) => {
              const progress = uploadProgress[item.id];
              return <div key={item.id} className="flex min-w-0 max-w-full items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-700">
                <AttachmentIcon kind={fileType(item.file)} />
                <span className="min-w-0 max-w-40 truncate" title={item.file.name}>{item.file.name}</span>
                <span className="shrink-0 text-slate-500">{progress?.state === "done" ? T("已上传")
                  : progress?.state === "error" ? T("失败，可重试")
                    : progress?.state === "offline" ? T("等待网络恢复")
                      : progress?.state === "retrying" ? T("重试中")
                    : progress ? `${progress.pct}%` : fmtBytes(item.file.size)}</span>
                <button type="button" onClick={() => removeFile(item.id)} disabled={sending}
                  className="rounded p-1 text-slate-500 hover:text-destructive disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                  aria-label={T("移除附件 {0}", [item.file.name])} data-testid={`dv3-presign-discussion-remove-file-${item.id}`}>
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </div>;
            })}
          </div>}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-slate-500">{T("新增内容将通过站内消息提醒对方。")}</span>
            <div className="flex flex-wrap items-center gap-2">
              {canAttach && <>
                <input ref={fileInputRef} type="file" multiple accept={DESIGN_UPLOAD_ACCEPT} className="hidden"
                  onChange={addFiles} disabled={sending} aria-label={T("选择讨论附件")}
                  data-testid="dv3-presign-discussion-file-input" />
                <Button type="button" size="sm" variant="outline" onClick={() => fileInputRef.current?.click()}
                  disabled={sending || files.length >= MAX_FILES} data-testid="dv3-presign-discussion-attach">
                  <Paperclip className="h-3.5 w-3.5" aria-hidden="true" />{T("添加附件")}{files.length ? ` (${files.length})` : ""}
                </Button>
              </>}
              <Button type="submit" size="sm" disabled={sending || (!text.trim() && (activeKind === "requirement" || files.length === 0)) || (files.length > 0 && !canAttach)} data-testid="dv3-presign-discussion-submit">
              {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Send className="h-3.5 w-3.5" aria-hidden="true" />}
              {activeKind === "requirement" ? T("追加要求") : T("发送留言")}
              </Button>
            </div>
          </div>
          {files.length > 0 && activeKind === "requirement" && !text.trim() && <p className="text-xs text-slate-500">{T("补充要求需填写文字；仅发送附件时可选择「留言讨论」。")}</p>}
          {files.length > 0 && !canAttach && <p className="text-xs text-destructive">{T("当前没有发送讨论附件的权限。")}</p>}
        </form> : <p className="text-xs text-slate-500">{T("当前仅可查看方案要求与讨论记录。")}</p>}
      </>}
      {feedback && <p id="presign-discussion-feedback" role={feedback.error ? "alert" : "status"}
        className={`text-xs ${feedback.error ? "text-destructive" : "text-slate-600"}`}>{feedback.text}</p>}
      {lightbox && <MediaLightbox tid="dv3-presign-discussion-lightbox" items={lightbox.items} index={lightbox.index}
        onIndex={(index) => setLightbox((current) => ({ ...current, index }))} onClose={() => setLightbox(null)} api={api} />}
    </section>
  );
}
